/**
 * ============================================================================
 * WALLET STORE - ZUSTAND
 * ============================================================================
 * 
 * Global wallet state management
 * 
 * @typedef {Object} Wallet
 * @typedef {Object} Balance
 * @property {number} available
 * @property {number} pending
 * @property {number} locked
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { validateObject, validateWallet, validateBalance, validateId } from './utils/validators';
import { addItemOptimized, updateItemInArray, limitArraySize, deduplicateById } from './utils/performance';
import { createSelector } from './utils/selectors';

const useWalletStore = create(
  persist(
    (set, get) => ({
      // State
      wallet: null,
      balance: {
        available: 0,
        pending: 0,
        locked: 0,
      },
      transactions: [],
      withdrawals: [],
      withdrawalMethods: [],
      statistics: null,
      loading: false,
      error: null,
      filters: {
        type: null,
        status: null,
        dateRange: null,
      },
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0,
      },

      // Actions
      /**
       * Set wallet with validation
       * @param {Wallet} wallet - Wallet object
       */
      setWallet: (wallet) => {
        if (wallet !== null) {
          validateWallet(wallet);
        }
        set({
          wallet,
          balance: wallet
            ? {
                available: wallet.availableBalance || 0,
                pending: wallet.pendingBalance || 0,
                locked: wallet.lockedBalance || 0,
              }
            : { available: 0, pending: 0, locked: 0 },
        });
      },

      /**
       * Update balance with validation
       * @param {Balance} balance - Balance updates
       * @param {boolean} optimistic - Whether this is an optimistic update
       */
      updateBalance: (balance, optimistic = false) => {
        validateBalance(balance);
        set((state) => ({
          balance: { ...state.balance, ...balance },
          wallet: state.wallet
            ? {
                ...state.wallet,
                availableBalance: balance.available ?? state.wallet.availableBalance,
                pendingBalance: balance.pending ?? state.wallet.pendingBalance,
                lockedBalance: balance.locked ?? state.wallet.lockedBalance,
              }
            : null,
        }));
      },

      /**
       * Set transactions with validation and size limit
       * @param {Array} transactions - Transactions array
       */
      setTransactions: (transactions) => {
        if (!Array.isArray(transactions)) {
          throw new Error('Transactions must be an array');
        }
        const limited = limitArraySize(transactions, 1000);
        set({ transactions: limited });
      },

      /**
       * Add transaction with validation and deduplication
       * @param {Object} transaction - Transaction object
       */
      addTransaction: (transaction) => {
        validateObject(transaction, 'Transaction');
        if (!transaction.id) {
          throw new Error('Transaction must have an id');
        }
        set((state) => ({
          transactions: addItemOptimized(state.transactions, transaction, {
            idKey: 'id',
            maxSize: 1000,
            prepend: true,
          }),
        }));
      },

      setWithdrawals: (withdrawals) => {
        set({ withdrawals });
      },

      /**
       * Add withdrawal with validation and deduplication
       * @param {Object} withdrawal - Withdrawal object
       */
      addWithdrawal: (withdrawal) => {
        validateObject(withdrawal, 'Withdrawal');
        validateId(withdrawal.id, 'Withdrawal ID');
        set((state) => ({
          withdrawals: addItemOptimized(state.withdrawals, withdrawal, {
            idKey: 'id',
            maxSize: 500,
            prepend: true,
          }),
        }));
      },

      /**
       * Update withdrawal with validation
       * @param {string} withdrawalId - Withdrawal ID
       * @param {Object} updates - Updates to apply
       */
      updateWithdrawal: (withdrawalId, updates) => {
        validateId(withdrawalId, 'Withdrawal ID');
        validateObject(updates, 'Updates');
        set((state) => ({
          withdrawals: updateItemInArray(state.withdrawals, withdrawalId, updates),
        }));
      },

      setWithdrawalMethods: (methods) => {
        set({ withdrawalMethods: methods });
      },

      setStatistics: (statistics) => {
        set({ statistics });
      },

      setFilters: (filters) => {
        set((state) => ({
          filters: { ...state.filters, ...filters },
          pagination: { ...state.pagination, page: 1 },
        }));
      },

      clearFilters: () => {
        set({
          filters: {
            type: null,
            status: null,
            dateRange: null,
          },
          pagination: { ...get().pagination, page: 1 },
        });
      },

      setPagination: (pagination) => {
        set((state) => ({
          pagination: { ...state.pagination, ...pagination },
        }));
      },

      setLoading: (loading) => {
        set({ loading });
      },

      setError: (error) => {
        set({ error });
      },

      clearError: () => {
        set({ error: null });
      },

      reset: () => {
        set({
          wallet: null,
          balance: {
            available: 0,
            pending: 0,
            locked: 0,
          },
          transactions: [],
          withdrawals: [],
          withdrawalMethods: [],
          statistics: null,
          loading: false,
          error: null,
          filters: {
            type: null,
            status: null,
            dateRange: null,
          },
          pagination: {
            page: 1,
            pageSize: 10,
            total: 0,
          },
        });
      },
    }),
    {
      name: 'wallet-storage',
      partialize: (state) => ({
        balance: state.balance,
        // Don't persist transactions, withdrawals - fetch fresh
      }),
    }
  )
);

// Memoized Selectors
export const selectTotalBalance = createSelector(
  (state) => {
    const { balance } = state;
    return (balance?.available || 0) + (balance?.pending || 0) + (balance?.locked || 0);
  }
);

export const selectWalletStats = createSelector(
  (state) => {
    const { transactions, withdrawals, balance } = state;
    return {
      totalTransactions: transactions?.length || 0,
      totalWithdrawals: withdrawals?.length || 0,
      pendingWithdrawals: withdrawals?.filter((w) => w.status === 'pending').length || 0,
      totalBalance: (balance?.available || 0) + (balance?.pending || 0) + (balance?.locked || 0),
    };
  }
);

export default useWalletStore;
